// src/components/AdminDashboard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import './AdminDashboard.css';
import './AdminDashboardModern.css';
import './AdminDashboardHome.css';

const ModuleIcon = ({ id }) => {
  const icons = {
    insights: <><path d="M4 19V10" /><path d="M10 19V5" /><path d="M16 19v-7" /><path d="M22 19H2" /></>,
    'mail-marketing': <><path d="m3 11 18-8-6.5 18-3.7-7.8L3 11Z" /><path d="m10.8 13.2 4.8-4.8" /></>,
    'client-manager': <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    'digital-ads': <><path d="m3 11 17-7v16L3 13v-2Z" /><path d="M11.6 16.5 10 21H6l1.4-6" /><path d="M3 10v4" /></>,
    'blog-manager': <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h6" /></>,
    reviews: <path d="m12 2.8 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.4l6.2-.9L12 2.8Z" />,
    'email-manager': <><rect x="2.5" y="4.5" width="19" height="15" rx="2" /><path d="m3.5 6 8.5 7 8.5-7" /></>,
    'agent-referral': <><circle cx="8" cy="12" r="3" /><circle cx="18" cy="6" r="2.5" /><circle cx="18" cy="18" r="2.5" /><path d="m10.7 10.7 5-3.4" /><path d="m10.7 13.3 5 3.4" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.09A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.2 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2.4v-4h.09A1.7 1.7 0 0 0 4.2 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 8.6 4.2a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V2.4h4v.09A1.7 1.7 0 0 0 15 4.2a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 8.6a1.7 1.7 0 0 0 .6 1 1.7 1.7 0 0 0 1.1.4h.1v4h-.09A1.7 1.7 0 0 0 19.4 15Z" /></>
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icons[id]}
    </svg>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const adminModules = [
    {
      id: 'insights',
      title: 'Analytics & Insights',
      description: 'View performance metrics, lead analytics, and business intelligence reports',
      category: 'Intelligence',
      color: '#1B96FF',
      path: '/admin/insights'
    },
    {
      id: 'mail-marketing',
      title: 'Paper Mail Marketing',
      description: 'Create, manage, and track physical mail campaigns and direct mail sequences',
      category: 'Campaigns',
      color: '#FF6B35',
      path: '/admin/mail-marketing'
    },
    {
      id: 'client-manager',
      title: 'Client Management',
      description: 'Manage client relationships, track interactions, and view client history',
      category: 'Relationships',
      color: '#4CAF50',
      path: '/admin/client-manager'
    },
    {
      id: 'digital-ads',
      title: 'Digital Advertising',
      description: 'Manage Facebook, Google, and social media advertising campaigns',
      category: 'Campaigns',
      color: '#9C27B0',
      path: '/admin/digital-ads'
    },
    {
      id: 'blog-manager',
      title: 'Content Management',
      description: 'Create, edit, and publish blog posts and marketing content',
      category: 'Publishing',
      color: '#FF9800',
      path: '/admin/blog-manager'
    },
    {
      id: 'reviews',
      title: 'Review Management',
      description: 'Monitor, respond to, and manage customer reviews and testimonials',
      category: 'Reputation',
      color: '#FFC107',
      path: '/admin/reviews'
    },
    {
      id: 'email-manager',
      title: 'Email Manager',
      description: 'Create email templates and setup automated drip campaigns',
      category: 'Campaigns',
      color: '#17a2b8',
      path: '/admin/email-manager'
    },
    {
      id: 'agent-referral',
      title: 'Agent Referral',
      description: 'Refer agents and track your referral submissions',
      category: 'Network',
      color: '#e74c3c',
      path: '/admin/agent-referral'
    },
    {
      id: 'settings',
      title: 'System Settings',
      description: 'Configure account settings, preferences, and system configurations',
      category: 'Administration',
      color: '#607D8B',
      path: '/admin/settings'
    }
  ];

  return (
    <div className="admin-dashboard-modern admin-dashboard-home">
      <header className="admin-home-header">
        <nav className="admin-home-nav" aria-label="Admin navigation">
          <div className="admin-home-brand">
            <span className="admin-home-brand-mark" aria-hidden="true">
              <svg viewBox="0 0 32 32" fill="none">
                <path d="M5 20.5 16 7l11 13.5" />
                <path d="M9 17v9h14v-9" />
                <path d="M13 26v-7h6v7" />
              </svg>
            </span>
            <span className="admin-home-brand-copy">
              <small>Agent Winds</small>
              <strong>Operations</strong>
            </span>
          </div>

          <div className="admin-home-nav-actions">
            <span className="admin-home-secure-status"><i aria-hidden="true" /> Secure workspace</span>
            <button type="button" onClick={() => navigate('/')} className="admin-home-site-button">
              View website
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5" /></svg>
            </button>
          </div>
        </nav>
      </header>

      <main className="admin-home-main">
        <section className="admin-home-hero" aria-labelledby="admin-home-title">
          <div className="admin-home-hero-copy">
            <p className="admin-home-eyebrow">Private business workspace</p>
            <h1 id="admin-home-title">Your business, in command.</h1>
            <p className="admin-home-intro">
              One focused workspace for {SITE_CONFIG.AGENT.NAME}'s clients, campaigns, content, and growth.
            </p>
            <div className="admin-home-hero-meta" aria-label="Workspace information">
              <span>{adminModules.length} operational modules</span>
              <span>Site · {SITE_CONFIG.SITE_ID}</span>
            </div>
          </div>

          <button type="button" className="admin-home-ai-card" onClick={() => navigate('/admin/agent-winds')}>
            <span className="admin-home-ai-glow" aria-hidden="true" />
            <span className="admin-home-ai-topline">
              <span className="admin-home-ai-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none"><path d="M4 7h7M2 12h12M5 17h6" /><path d="M15.5 5.5c2.8 0 5 2.2 5 5 0 1.6-.7 3-1.8 3.9.1.6.5 1.1 1.1 1.4l1.2.6-1.1 2.1-1.2-.6a4.7 4.7 0 0 1-2.3-2.4h-.9a5 5 0 1 1 0-10Z" /></svg>
              </span>
              <span className="admin-home-ai-state"><i aria-hidden="true" /> AI portal</span>
            </span>
            <span className="admin-home-ai-copy">
              <strong>Agent Winds</strong>
              <span>Run intelligent workflows and turn requests into completed work.</span>
            </span>
            <span className="admin-home-ai-action">
              Open AI workspace
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5" /></svg>
            </span>
          </button>
        </section>

        <section className="admin-home-workspace" aria-labelledby="admin-workspace-title">
          <div className="admin-home-section-heading">
            <div>
              <p className="admin-home-eyebrow">Operations</p>
              <h2 id="admin-workspace-title">Workspace</h2>
            </div>
            <p>Select a module to manage that part of the business.</p>
          </div>

          <div className="admin-home-modules-grid">
            {adminModules.map((module, index) => (
              <button
                type="button"
                key={module.id}
                className="admin-home-module-card"
                onClick={() => navigate(module.path)}
                style={{ '--module-color': module.color, '--module-index': index }}
                aria-label={`Open ${module.title}`}
              >
                <span className="admin-home-module-topline">
                  <span className="admin-home-module-icon"><ModuleIcon id={module.id} /></span>
                  <span className="admin-home-module-category">{module.category}</span>
                </span>
                <span className="admin-home-module-copy">
                  <strong>{module.title}</strong>
                  <span>{module.description}</span>
                </span>
                <span className="admin-home-module-footer">
                  <span>Open module</span>
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5" /></svg>
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;
